import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Home, 
  MapPin, 
  CreditCard,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Wallet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currency, formatPrice, currencies } = useCurrency();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [gateways, setGateways] = useState([]);
  const [wallet, setWallet] = useState(null);
  
  const [bookingData, setBookingData] = useState({
    booking_type: 'home_collection',
    appointment_date: null,
    appointment_time: '',
    address: user?.address || '',
    notes: '',
    currency: currency,
    payment_method: 'gateway', // 'gateway' or 'wallet'
    payment_gateway: 'stripe'
  });

  const timeSlots = [
    '07:00 AM', '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM',
    '05:00 PM', '06:00 PM'
  ];

  useEffect(() => {
    fetchTestDetails();
    fetchGateways();
    fetchWallet();
  }, [testId]);

  const fetchTestDetails = async () => {
    try {
      const response = await axios.get(`${API}/tests/${testId}?currency=${currency}`);
      setTest(response.data);
    } catch (error) {
      console.error('Error fetching test:', error);
      toast.error('Test not found');
      navigate('/tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchGateways = async () => {
    try {
      const response = await axios.get(`${API}/payment-gateways`);
      setGateways(response.data.filter(g => g.enabled));
    } catch (error) {
      console.error('Error fetching gateways:', error);
    }
  };

  const fetchWallet = async () => {
    try {
      const response = await axios.get(`${API}/wallet`);
      setWallet(response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  const handleChange = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    if (step === 1) {
      return bookingData.booking_type !== '';
    }
    if (step === 2) {
      return bookingData.appointment_date && bookingData.appointment_time;
    }
    if (step === 3) {
      if (bookingData.booking_type === 'home_collection') {
        return bookingData.address.trim() !== '';
      }
      return true;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        test_ids: [testId],
        booking_type: bookingData.booking_type,
        appointment_date: format(bookingData.appointment_date, 'yyyy-MM-dd'),
        appointment_time: bookingData.appointment_time,
        address: bookingData.address,
        notes: bookingData.notes,
        currency: bookingData.currency
      };

      const response = await axios.post(`${API}/bookings`, payload);
      const booking = response.data;

      if (bookingData.payment_method === 'wallet') {
        // Pay from wallet
        await axios.post(`${API}/wallet/pay-from-wallet?booking_id=${booking.id}`);
        toast.success('Payment successful!');
        navigate('/bookings');
      } else {
        // Create payment checkout
        const checkoutResponse = await axios.post(
          `${API}/payments/create-checkout?booking_id=${booking.id}&origin_url=${window.location.origin}&gateway=${bookingData.payment_gateway}`
        );
        
        // Handle bank transfer differently - no redirect URL
        if (bookingData.payment_gateway === 'bank_transfer') {
          // Navigate to payment success page with bank details
          navigate(`/payment-success?gateway=bank_transfer&reference=${checkoutResponse.data.reference}&booking_id=${booking.id}`);
        } else if (checkoutResponse.data.url) {
          window.location.href = checkoutResponse.data.url;
        } else {
          toast.error('Failed to get payment URL');
          setSubmitting(false);
        }
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
      setSubmitting(false);
    }
  };

  const getPrice = () => {
    return test?.converted_price || test?.price || 0;
  };

  const getGatewayLabel = (gateway) => {
    const labels = {
      stripe: { name: 'Stripe', icon: '💳', desc: 'Credit/Debit Card' },
      paystack: { name: 'Paystack', icon: '🏦', desc: 'Cards, Bank Transfer' },
      monnify: { name: 'Monnify', icon: '🏧', desc: 'Nigerian Banks' },
      bank_transfer: { name: 'Bank Transfer', icon: '🏛️', desc: 'Direct Bank Transfer' }
    };
    return labels[gateway] || { name: gateway, icon: '💰', desc: '' };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center py-32">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="booking-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/tests/${testId}`)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Test Details
        </Button>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {[
            { num: 1, label: 'Collection Type' },
            { num: 2, label: 'Date & Time' },
            { num: 3, label: 'Details' },
            { num: 4, label: 'Payment' }
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-medium ${
                step >= s.num ? 'bg-sky-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {step > s.num ? <CheckCircle2 className="w-5 h-5" /> : s.num}
              </div>
              <span className={`ml-2 text-sm hidden sm:inline ${step >= s.num ? 'text-slate-900' : 'text-slate-500'}`}>
                {s.label}
              </span>
              {idx < 3 && <div className={`w-8 sm:w-16 h-0.5 mx-2 ${step > s.num ? 'bg-sky-600' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="card-clinical">
              {/* Step 1: Collection Type */}
              {step === 1 && (
                <div className="animate-fade-in" data-testid="step-1">
                  <h2 className="text-xl font-bold text-slate-900 mb-6">Choose Collection Type</h2>
                  <RadioGroup
                    value={bookingData.booking_type}
                    onValueChange={(v) => handleChange('booking_type', v)}
                    className="space-y-4"
                  >
                    <div className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                      bookingData.booking_type === 'home_collection' ? 'border-sky-600 bg-sky-50' : 'hover:border-slate-300'
                    }`}>
                      <RadioGroupItem value="home_collection" id="home" className="mt-1" />
                      <label htmlFor="home" className="ml-3 cursor-pointer flex-1">
                        <div className="flex items-center gap-2">
                          <Home className="w-5 h-5 text-sky-600" />
                          <span className="font-semibold text-slate-900">Home Collection</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          Our phlebotomist will visit your location for sample collection
                        </p>
                      </label>
                    </div>
                    <div className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                      bookingData.booking_type === 'walk_in' ? 'border-sky-600 bg-sky-50' : 'hover:border-slate-300'
                    }`}>
                      <RadioGroupItem value="walk_in" id="walkin" className="mt-1" />
                      <label htmlFor="walkin" className="ml-3 cursor-pointer flex-1">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-sky-600" />
                          <span className="font-semibold text-slate-900">Walk-in Visit</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          Visit our nearest diagnostic center for sample collection
                        </p>
                      </label>
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Step 2: Date & Time */}
              {step === 2 && (
                <div className="animate-fade-in" data-testid="step-2">
                  <h2 className="text-xl font-bold text-slate-900 mb-6">Select Date & Time</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="mb-2 block">Appointment Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal h-11"
                            data-testid="date-picker-trigger"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {bookingData.appointment_date 
                              ? format(bookingData.appointment_date, 'PPP')
                              : 'Select date'
                            }
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={bookingData.appointment_date}
                            onSelect={(date) => handleChange('appointment_date', date)}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return date < today;
                            }}
                            initialFocus
                            data-testid="calendar"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="mb-2 block">Appointment Time *</Label>
                      <Select 
                        value={bookingData.appointment_time} 
                        onValueChange={(v) => handleChange('appointment_time', v)}
                      >
                        <SelectTrigger className="h-11" data-testid="time-select">
                          <Clock className="mr-2 h-4 w-4" />
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Details */}
              {step === 3 && (
                <div className="animate-fade-in" data-testid="step-3">
                  <h2 className="text-xl font-bold text-slate-900 mb-6">
                    {bookingData.booking_type === 'home_collection' ? 'Collection Address' : 'Additional Details'}
                  </h2>
                  {bookingData.booking_type === 'home_collection' && (
                    <div className="mb-4">
                      <Label htmlFor="address">Address *</Label>
                      <Textarea
                        id="address"
                        value={bookingData.address}
                        onChange={(e) => handleChange('address', e.target.value)}
                        placeholder="Enter your complete address with landmark"
                        className="mt-1 min-h-24"
                        data-testid="address-input"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="notes">Special Instructions (Optional)</Label>
                    <Textarea
                      id="notes"
                      value={bookingData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      placeholder="Any special instructions or medical conditions to note"
                      className="mt-1 min-h-24"
                      data-testid="notes-input"
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Payment Summary */}
              {step === 4 && (
                <div className="animate-fade-in" data-testid="step-4">
                  <h2 className="text-xl font-bold text-foreground mb-6">Review & Pay</h2>
                  <div className="space-y-4">
                    <div className="flex justify-between py-3 border-b border-border">
                      <span className="text-muted-foreground">Test</span>
                      <span className="font-medium text-foreground">{test?.name}</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-border">
                      <span className="text-muted-foreground">Collection Type</span>
                      <span className="font-medium text-foreground capitalize">
                        {bookingData.booking_type.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-border">
                      <span className="text-muted-foreground">Date & Time</span>
                      <span className="font-medium text-foreground">
                        {bookingData.appointment_date && format(bookingData.appointment_date, 'PPP')} at {bookingData.appointment_time}
                      </span>
                    </div>
                    {bookingData.booking_type === 'home_collection' && (
                      <div className="flex justify-between py-3 border-b border-border">
                        <span className="text-muted-foreground">Address</span>
                        <span className="font-medium text-foreground text-right max-w-xs truncate">
                          {bookingData.address}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between py-3 text-lg">
                      <span className="font-semibold text-foreground">Total Amount</span>
                      <span className="font-bold text-sky-600">{formatPrice(getPrice(), bookingData.currency)}</span>
                    </div>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="mt-6">
                    <Label className="mb-3 block">Payment Method</Label>
                    <RadioGroup
                      value={bookingData.payment_method}
                      onValueChange={(v) => handleChange('payment_method', v)}
                      className="space-y-3"
                    >
                      {/* Wallet Payment */}
                      <div className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                        bookingData.payment_method === 'wallet' ? 'border-sky-600 bg-sky-50 dark:bg-sky-900/20' : 'hover:border-border'
                      } ${(!wallet || wallet.balance < getPrice()) ? 'opacity-50' : ''}`}>
                        <RadioGroupItem 
                          value="wallet" 
                          id="wallet" 
                          className="mt-1"
                          disabled={!wallet || wallet.balance < getPrice()}
                        />
                        <label htmlFor="wallet" className="ml-3 cursor-pointer flex-1">
                          <div className="flex items-center gap-2">
                            <Wallet className="w-5 h-5 text-sky-600" />
                            <span className="font-semibold text-foreground">Pay from Wallet</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            Balance: {formatPrice(wallet?.balance || 0, wallet?.currency || 'USD')}
                            {(!wallet || wallet.balance < getPrice()) && ' (Insufficient)'}
                          </p>
                        </label>
                      </div>

                      {/* Gateway Payment */}
                      <div className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                        bookingData.payment_method === 'gateway' ? 'border-sky-600 bg-sky-50 dark:bg-sky-900/20' : 'hover:border-border'
                      }`}>
                        <RadioGroupItem value="gateway" id="gateway" className="mt-1" />
                        <label htmlFor="gateway" className="ml-3 cursor-pointer flex-1">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-sky-600" />
                            <span className="font-semibold text-foreground">Pay with Card/Bank</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            Pay securely using our payment partners
                          </p>
                        </label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Gateway Selection */}
                  {bookingData.payment_method === 'gateway' && (
                    <div className="mt-4">
                      <Label className="mb-2 block">Select Payment Gateway</Label>
                      <Select 
                        value={bookingData.payment_gateway} 
                        onValueChange={(v) => handleChange('payment_gateway', v)}
                      >
                        <SelectTrigger data-testid="gateway-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {gateways.map((g) => {
                            const label = getGatewayLabel(g.id);
                            return (
                              <SelectItem key={g.id} value={g.id}>
                                <span className="flex items-center gap-2">
                                  <span>{label.icon}</span>
                                  <span>{label.name}</span>
                                  <span className="text-muted-foreground text-xs">- {label.desc}</span>
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="mt-6 p-4 bg-sky-50 dark:bg-sky-900/20 rounded-lg">
                    <p className="text-sm text-sky-800 dark:text-sky-300">
                      <CreditCard className="w-4 h-4 inline mr-2" />
                      {bookingData.payment_method === 'wallet' 
                        ? 'Payment will be deducted from your wallet balance.'
                        : 'You will be redirected to our secure payment gateway to complete the transaction.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => setStep(s => Math.max(1, s - 1))}
                  disabled={step === 1}
                  data-testid="prev-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                {step < 4 ? (
                  <Button
                    className="bg-sky-600 hover:bg-sky-700"
                    onClick={() => {
                      if (validateStep()) {
                        setStep(s => s + 1);
                      } else {
                        toast.error('Please fill in all required fields');
                      }
                    }}
                    data-testid="next-btn"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    className="bg-sky-600 hover:bg-sky-700"
                    onClick={handleSubmit}
                    disabled={submitting}
                    data-testid="pay-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : bookingData.payment_method === 'wallet' ? (
                      <>
                        <Wallet className="w-4 h-4 mr-2" />
                        Pay {formatPrice(getPrice(), bookingData.currency)}
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay {formatPrice(getPrice(), bookingData.currency)}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="card-clinical sticky top-24">
              <h3 className="font-semibold text-foreground mb-4">Order Summary</h3>
              <div className="space-y-3 pb-4 border-b border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{test?.name}</span>
                  <span className="font-medium">{formatPrice(getPrice(), bookingData.currency)}</span>
                </div>
              </div>
              <div className="flex justify-between mt-4 text-lg">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-sky-600">{formatPrice(getPrice(), bookingData.currency)}</span>
              </div>
              
              {/* Currency Selection */}
              <div className="mt-4 pt-4 border-t border-border">
                <Label className="text-sm mb-2 block">Currency</Label>
                <Select value={bookingData.currency} onValueChange={(v) => handleChange('currency', v)}>
                  <SelectTrigger className="w-full" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="mt-4 pt-4 border-t border-border space-y-2 text-sm text-muted-foreground">
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Secure Payment
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Instant Confirmation
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Free Rescheduling
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BookingPage;
